import { Component, OnDestroy, ViewChild, IterableDiffer, IterableDiffers, inject, ChangeDetectorRef } from '@angular/core';
import { CompanyInformationComponent } from 'components/company/company-information.component';
import { CompanyProductsLedgerPostsComponent } from 'components/company/company-products-ledger-posts.component';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { ListViewComponent } from 'components/layout/list-view/list-view.component';
import { AccountingPlanStatus } from 'enums/AccountingPlanStatus';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { MessageType } from 'enums/MessageType';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { BillingInfoDtoModel } from 'models/generated/Models.BillingInfoDto';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { Pipes } from 'modules/pipes';
import { Subscription, firstValueFrom } from 'rxjs';
import { AccountingService } from 'services/accounting.service';
import { InvoicingService } from 'services/invoicing.service';
import { CompanyService } from 'services/company.service';
import { CredentialRequired } from 'services/credential.service';
import { RefreshService } from 'services/data-tracking';
import { ComponentUrl } from 'services/navigation.service';
import { ProductPlanService } from 'services/productplan.service';
import { PushService } from 'services/push.service';
import { QuestionnaireService } from 'services/questionnaire.service';
import { OrderLineModel } from 'models/generated/Entities.GormCrm.OrderLine';
import { QuestionnaireViewModel } from 'models/generated/Models.QuestionnaireView';
import { QuestionTagConstants } from 'constants/QuestionTagConstants';
import { NotificationService } from 'services/notification.service';
import { QuestionnaireComponent } from 'components/questionnaire/questionnaire.component';
import { SalesService } from 'services/sales.service';
import { OrderModel } from 'models/generated/Entities.GormCrm.Order';
import { AccountingPlanSearchRequestModel } from 'models/generated/Accounting.Api.Models.AccountingPlanRequest';

interface OrderPlanControlInfo {
	orderIds: string[];
	brandId?: string;
	company: CompanyModel;
	accountingPlans: AccountingPlanModel[];
	startedPlanCount: number;
	remainingNumberOfPlansToControl: number;
	earliestStartDate?: Date;
	totalPlanValue: number;
	latestOrder?: OrderModel;
}

@Component({
	selector: 'invoice-control-list',
	templateUrl: './invoice-control-list.component.html',
	providers: [RefreshService],
	imports: [Layout, ListView, CompanyInformationComponent, CompanyProductsLedgerPostsComponent, Pipes, QuestionnaireComponent]
})
@ComponentUrl('invoice-control-list')
@CredentialRequired(CredentialType.UpdateAccountingPlanStatus)
export class InvoiceControlListComponent implements OnDestroy {
	@ViewChild('confirmSingleButton') confirmSingleButton?: SubmitButtonComponent;
	@ViewChild('confirmButton') confirmButton?: SubmitButtonComponent;
	@ViewChild('companyList') companyList?: ListViewComponent;
	@ViewChild('accountingPlanList') accountingPlanList?: ListViewComponent;

	private refreshSubscription: Subscription;
	private accountingPlanSubscription: Subscription;
	private productPlanSubscription: Subscription;

	private iterableDiffers = inject(IterableDiffers);
	private accountingPlansDiffer?: IterableDiffer<AccountingPlanModel>;
	private accountingPlans?: AccountingPlanModel[];
	private orders: OrderModel[] = [];

	ordersToControl?: OrderPlanControlInfo[];
	customerNo?: string;
	loadingBillingInfo = true;
	selectedAccountingPlan?: AccountingPlanModel;
	hasSpecialCondition?: boolean = undefined;
	questionnaire?: QuestionnaireViewModel = undefined;
	loadingQuestionnaire = true;

	private _selected?: OrderPlanControlInfo;
	set selected(planInfo: OrderPlanControlInfo | undefined) {
		if (this._selected == planInfo) return;

		this._selected = undefined;
		this.cdr.detectChanges(); // Enforces reload of sup-panel
		this._selected = planInfo;
		if (!planInfo) return;

		this.hasSpecialCondition = undefined;
		this.questionnaire = undefined;
		this.selectedAccountingPlan = undefined;

		this.loadQuestionnaire(planInfo.accountingPlans.flatMap(ap => (ap.productPlan?.orderLines ?? []).map((ol: OrderLineModel) => ol.id)));
		this.loadCompanyInfo(planInfo.company.id!);
	}

	async loadQuestionnaire(orderLineIds: string[]) {
		this.loadingQuestionnaire = true;
		this.questionnaire = await this.questionnaireService.getQuestionnaireViewByTag('approve-order', orderLineIds);
		this.hasSpecialCondition = this.isApproveOrderSpecialConditionsAnsweredAsTrue(this.questionnaire);
		this.loadingQuestionnaire = false;
	}
	async loadCompanyInfo(companyId: string) {
		this.loadingBillingInfo = true;
		this.billingInfo = await this.companyService.getCompanyBillingInfo(companyId);
		this.loadingBillingInfo = false;
	}

	isApproveOrderSpecialConditionsAnsweredAsTrue(view: QuestionnaireViewModel): boolean {
		for (const question of view.questionCategories.flatMap(qc => qc.questionGroups ?? []).flatMap(qg => qg.questions ?? [])) {
			if (!(question.questionTags ?? []).some(t => t?.tag === QuestionTagConstants.approveOrderSpecialConditions)) continue;
			if ((question.replyData ?? []).some(rd => (rd.replies ? JSON.parse(rd.replies) : []).some((reply: boolean) => reply == true))) return true;
		}
		return false;
	}

	get selected() { return this._selected; }
	inputTypeEnum = InputType;
	billingInfo?: BillingInfoDtoModel;
	companyProductPlans?: ProductPlanModel[];
	accountingPlanStatusEnum = AccountingPlanStatus;

	constructor(public accountingService: AccountingService,
		private readonly productPlanService: ProductPlanService,
		private readonly pushService: PushService,
		private readonly invoicingService: InvoicingService,
		private readonly companyService: CompanyService,
		private readonly questionnaireService: QuestionnaireService,
		private readonly salesService: SalesService,
		readonly refreshService: RefreshService,
		private readonly notificationService: NotificationService,
		private cdr: ChangeDetectorRef) {

		this.reloadList();
		this.refreshSubscription = refreshService.onRefresh.subscribe(() => { this.reloadList() });

		this.accountingPlanSubscription = this.pushService.onChanges<{ accountingPlanId: string, companyId: string }>(MessageType.AccountingPlanChanges).subscribe(async (event) => {
			const accountingPlanId = event.data?.accountingPlanId;
			const oldAccountingPlan = this.accountingPlans?.find(ap => ap.id == accountingPlanId);
			if (accountingPlanId && oldAccountingPlan) await this.reloadAccountingPlan(oldAccountingPlan);
		});

		this.productPlanSubscription = this.pushService.onChanges<{ productPlanId: string }>(MessageType.ProductPlanChanges).subscribe(async (event) => {
			const productPlanId = event.data?.productPlanId;
			const oldAccountingPlan = this.accountingPlans?.find(ap => ap.productPlanId == productPlanId);
			if (productPlanId && oldAccountingPlan) await this.reloadAccountingPlan(oldAccountingPlan);
		});
	}

	ngOnDestroy() {
		this.refreshSubscription.unsubscribe();
		this.accountingPlanSubscription.unsubscribe();
		this.productPlanSubscription.unsubscribe();
	}

	async reloadAccountingPlan(oldAccountingPlan: AccountingPlanModel) {

		// Reload AccountingPlan, and update plan in list
		const updatedAccountingPlan = await this.accountingService.getAccountingPlan(oldAccountingPlan.id!);
		const updatedProductPlan = await this.productPlanService.getProductPlan(updatedAccountingPlan.productPlanId!);

		Object.assign(oldAccountingPlan, updatedAccountingPlan);
		Object.assign(oldAccountingPlan.productPlan, updatedProductPlan);
	} 

	async reloadList(): Promise<void> {
		this.accountingPlans = undefined;
		this.ordersToControl = undefined;

		let searchRequest = new AccountingPlanSearchRequestModel();
		searchRequest.status = AccountingPlanStatus.None;
		searchRequest.hasStartDate = false;

		const accountingPlans = (await Promise.all([
			this.accountingService.getAccountingPlansByStatus(AccountingPlanStatus.Control),
			this.accountingService.searchAccountingPlans(searchRequest)
		])).flat();

		const productPlans = await this.productPlanService.getProductPlans(accountingPlans.map(plan => plan.productPlanId!));
		this.orders = await this.salesService.getOrdersByIds(productPlans.flatMap(pp => pp.orderLines.map(ol => ol.orderId!)));

		accountingPlans.forEach(plan => {
			plan.productPlan = productPlans.find(pp => pp.id === plan.productPlanId);
		});
		this.accountingPlans = accountingPlans.filter(plan => plan.productPlan);
		this.accountingPlansDiffer = this.iterableDiffers.find(this.accountingPlans).create();
	}

	ngDoCheck() {
		const diff = this.accountingPlansDiffer?.diff(this.accountingPlans);
		if (!this.accountingPlans || !diff) return;
		if (!this.ordersToControl) this.ordersToControl = [];

		diff.forEachAddedItem(record => {
			const productPlan = record.item.productPlan as ProductPlanModel;
			const orderIds = productPlan.orderLines.flatMap(ol => ol.orderId);
			const latestOrder = this.orders.filter(o => orderIds.includes(o.id!)).sort((a, b) => b.signDate!.getTime() - a.signDate!.getTime())[0];

			let planInfo = this.ordersToControl?.find(i => i.orderIds.some(orderId => orderIds.includes(orderId)));
			if (planInfo) {
				this.updatePlanInfo(planInfo);
				return;
			}
			planInfo = { company: productPlan.company, orderIds, accountingPlans: [record.item], latestOrder } as OrderPlanControlInfo;
			this.updatePlanInfo(planInfo);
			if (planInfo.startedPlanCount > 0) this.ordersToControl?.push(planInfo);
		});
		diff.forEachRemovedItem(record => {
			if (!this.ordersToControl) return;

			const planInfo = this.ordersToControl.find(i => i.accountingPlans.some(plan => plan.id == record.item.id));
			if (!planInfo) return;

			planInfo.accountingPlans.splice(planInfo.accountingPlans.findIndex(plan => plan.id == record.item.id), 1);
			this.updatePlanInfo(planInfo);

			if (planInfo.startedPlanCount == 0) this.ordersToControl.splice(this.ordersToControl.indexOf(planInfo), 1);
		});
	}
	updatePlanInfo(planInfo: OrderPlanControlInfo) {
		if (planInfo.orderIds.length > 0) planInfo.accountingPlans = this.accountingPlans!.filter(ap => ap.productPlan.orderLines.some((line: OrderLineModel) => planInfo.orderIds.includes(line.orderId!)));
		planInfo.brandId = planInfo.accountingPlans[0]?.brandId; // We assume all grouped plans belong to the same brand. This isn't reinforced by business logic, but it's just a visual guide anyway
		planInfo.startedPlanCount = planInfo.accountingPlans.filter(p => p.startDate).length;
		planInfo.remainingNumberOfPlansToControl = planInfo.accountingPlans.filter(p => p.startDate && p.status == AccountingPlanStatus.Control).length;
		planInfo.earliestStartDate = planInfo.accountingPlans.filter(p => p.startDate).sort((a, b) => b.startDate!.getTime() - a.startDate!.getTime())[0]?.startDate;
		planInfo.totalPlanValue = planInfo.accountingPlans.reduce((sum, plan) => sum + plan.totalPlanValue, 0) ?? 0;
	}

	async clearControlStatus(plan?: AccountingPlanModel) {
		if (!this.selected) return this.resetButtons();
		let plansToUpdate: AccountingPlanModel[] = [];
		if (plan) {
			plansToUpdate = plan.startDate ? [plan] : [];
		}
		else {
			if (this.selected.startedPlanCount != this.selected.accountingPlans.length)
				return this.resetButtons();
			plansToUpdate = this.selected.accountingPlans.filter(p => p.startDate);
		}
		if (plansToUpdate.length == 0) return this.resetButtons();
		if (this.hasSpecialCondition && !await firstValueFrom(this.notificationService.confirm('accounting.invoice-control-list.confirm-clear-control-status-with-special-condition'))) {
			return this.resetButtons();
		}
		await Promise.all(plansToUpdate.map(plan => {
			plan.status = AccountingPlanStatus.None;
			return this.accountingService.setPlanStatus(AccountingPlanStatus.Control, AccountingPlanStatus.None, plan.id!)
		}));
		this.updatePlanInfo(this.selected);
		this.resetButtons();
		this.selectedAccountingPlan = undefined;
		if (this.selected.remainingNumberOfPlansToControl == 0) {
			if (this.companyList) this.companyList.selectNextRow();
			this.refreshService.refreshAvailable();
		}
	}

	private resetButtons() {
		if (this.confirmSingleButton) this.confirmSingleButton.reset();
		if (this.confirmButton) this.confirmButton.reset();
		return false;
	}

	getAnnualContractValue(accountingPlan: AccountingPlanModel): number | undefined {
		return this.accountingService.generateAnnualContractValue(accountingPlan?.startDate, accountingPlan?.endDate, accountingPlan?.totalPlanValue);
	}

	findProductPlan(productPlanId?: string) {
		return this.companyProductPlans?.find(pp => pp.id === productPlanId);
	}
}
